import type { TranslationKeys } from '@vocab/core';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  ButtonLink,
  IconAttachment,
  IconChecklist,
  IconDocument,
  IconNewWindow,
  IconResume,
  Inline,
  MenuItem,
  MenuRenderer,
} from 'braid-design-system';
import type { FC } from 'react';

import downloadFile from 'src/DocumentViewer/utils/downloadFile';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import {
  type ATTACHMENT_TYPES,
  COVER_LETTER,
  RESUME,
  SELECTION_CRITERIA,
} from 'src/constants/attachmentTypes';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { Display } from 'src/libs/Display';
import type { IAttachment } from 'src/stores/JobApplicationStore/Attachment';
import {
  attachmentUrlV2,
  NON_PDF_ATTACHMENT_TYPE_NUMBER_MAPPING,
} from 'src/utils/attachmentUrls';
import { trackDownloadDocumentEvent } from 'src/utils/events';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import { sortStoreAttachments } from 'src/utils/sortStoreAttachments';

import translations from '../.vocab';

const getLabelFromAttachmentType = (
  type: number,
): {
  label: TranslationKeys<typeof translations>;
  icon: JSX.Element;
} | null => {
  switch (type) {
    case RESUME.type:
      return {
        label: 'Resumé',
        icon: <IconResume />,
      };
    case COVER_LETTER.type:
      return {
        label: 'Cover letter',
        icon: <IconDocument />,
      };
    case SELECTION_CRITERIA.type:
      return {
        label: 'Selection criteria',
        icon: <IconChecklist />,
      };
    default:
      return null;
  }
};

interface ViewJobLinkAndAttachmentsProps {
  attachments: IAttachment[] | undefined;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  jobId: string;
  prospectId: string;
  adcentreCandidateId: string;
  applicationCorrelationId: string;
  seekCandidateId?: string | null;
  applicationId: string;
}

export const ViewJobLinkAndAttachments: FC<ViewJobLinkAndAttachmentsProps> = ({
  attachments,
  isVerifiedId,
  jobId,
  prospectId,
  adcentreCandidateId,
  applicationCorrelationId,
  seekCandidateId,
  applicationId,
}) => {
  const { t } = useTranslations(translations);
  const { router } = useAppConfig();

  const sortedAttachments = attachments
    ? sortStoreAttachments(attachments)
    : [];

  const otherAttachments = sortedAttachments.filter(
    (attachment) => attachment.filename !== null,
  );

  return (
    <Inline space={['medium', 'small']} alignY="center">
      <Box
        role="button"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Display tablet desktop>
          <ButtonLink
            aria-label={t('View application')}
            href={router.candidates({
              jobid: jobId,
              selected: prospectId,
              tab: JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS,
            })}
            icon={<IconNewWindow />}
            size="small"
            variant="transparent"
            bleed
            target="_blank"
          >
            {t('View application')}
          </ButtonLink>
        </Display>
        <Display mobile>
          <ButtonLink
            aria-label={t('View application')}
            href={router.candidates({
              jobid: jobId,
              selected: prospectId,
              tab: JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS,
            })}
            size="small"
            variant="transparent"
            bleed
            target="_blank"
          >
            <IconNewWindow />
          </ButtonLink>
        </Display>
      </Box>

      {sortedAttachments.length > 0
        ? sortedAttachments.map((attachment) => {
            const attachmentLabel = getLabelFromAttachmentType(attachment.type);

            if (attachmentLabel !== null) {
              const downloadUrl = attachmentUrlV2({
                jobId,
                applicationCorrelationId,
                attachmentType:
                  NON_PDF_ATTACHMENT_TYPE_NUMBER_MAPPING[
                    attachment.type as ATTACHMENT_TYPES
                  ],
                actionType: 'download',
                blobId: attachment.id,
                version: '0:0', // FIXME: refCheck wave 1 project eclipse scope, no reference check in previous apps tab
              });

              const onClickAttachmentButton = () =>
                downloadFile(downloadUrl, () => {
                  trackDownloadDocumentEvent({
                    adcentreCandidateId,
                    applicationCorrelationId,
                    seekCandidateId,
                    prospectId: attachment.jobApplication.id,
                    documentType: attachment.typeName,
                    blobId: attachment.id,
                    currentPage: TRACKING_CANDIDATE_DETAILS,
                    isVerifiedId,
                    applicationId,
                  });
                });

              return (
                <Box
                  key={attachment.id}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Display tablet desktop>
                    <Button
                      aria-label={t(attachmentLabel.label)}
                      icon={attachmentLabel.icon}
                      size="small"
                      variant="transparent"
                      bleed
                      onClick={onClickAttachmentButton}
                    >
                      {t(attachmentLabel.label)}
                    </Button>
                  </Display>

                  <Display mobile>
                    <Button
                      aria-label={t(attachmentLabel.label)}
                      size="small"
                      variant="transparent"
                      onClick={onClickAttachmentButton}
                      bleed
                    >
                      {attachmentLabel.icon}
                    </Button>
                  </Display>
                </Box>
              );
            }
          })
        : null}

      {otherAttachments.length > 0 ? (
        <MenuRenderer
          offsetSpace="small"
          width="small"
          trigger={(triggerProps) => (
            <Box userSelect="none" cursor="pointer" {...triggerProps}>
              <Display tablet desktop>
                <Button
                  aria-label={t('Other documents')}
                  icon={<IconAttachment />}
                  size="small"
                  variant="transparent"
                  bleed
                >
                  {t('Other documents')}
                </Button>
              </Display>
              <Display mobile>
                <Button
                  variant="transparent"
                  bleed
                  size="small"
                  aria-label={t('Other documents')}
                >
                  <IconAttachment />
                </Button>
              </Display>
            </Box>
          )}
        >
          {otherAttachments.map((item) => {
            const downloadUrl = attachmentUrlV2({
              jobId,
              applicationCorrelationId,
              attachmentType:
                NON_PDF_ATTACHMENT_TYPE_NUMBER_MAPPING[
                  item.type as ATTACHMENT_TYPES
                ],
              actionType: 'download',
              blobId: item.id,
              version: '0:0',
            });
            return (
              <MenuItem
                key={item.id}
                onClick={() =>
                  downloadFile(downloadUrl, () => {
                    trackDownloadDocumentEvent({
                      adcentreCandidateId,
                      applicationCorrelationId,
                      seekCandidateId,
                      prospectId: item.jobApplication.id,
                      documentType: item.typeName,
                      blobId: item.id,
                      currentPage: TRACKING_CANDIDATE_DETAILS,
                      isVerifiedId,
                      applicationId,
                    });
                  })
                }
              >
                {item.filename}
              </MenuItem>
            );
          })}
        </MenuRenderer>
      ) : null}
    </Inline>
  );
};
